import { useMemo } from 'react';
import { GetAuthData } from '../misc/GetAuthData';
import { GetLocalStorage } from '../misc/UseLocalStorage';

/**
 * Hook role-based access untuk komponen React.
 *
 * Mengembalikan flag praktis dan helper props untuk menerapkan mode
 * read-only pada admin biasa (level === 'admin'). Superadmin dan role
 * lainnya tetap punya akses mutasi penuh.
 *
 * Contoh pemakaian:
 *
 *   const { canMutate, readOnlyProps } = useRole();
 *   {canMutate && <Button onClick={onSave}>Simpan</Button>}
 *   <Input {...readOnlyProps} value={...} />
 */
export const useRole = () => {
  const authData = GetAuthData();
  const level: string | undefined =
    authData?.level ?? GetLocalStorage({ name: 'level' });

  return useMemo(() => {
    const isSuperAdmin = level === 'superadmin';
    const isAdminReadOnly = level === 'admin' ||level === 'adminhelpdesk';
    const canMutate = !isAdminReadOnly;
    const canResetPassword = canMutate || level === 'adminhelpdesk';

    return {
      level,
      isSuperAdmin,
      isAdminReadOnly,
      canMutate,
      canResetPassword,
      // Spread ke komponen Chakra Input/Textarea agar otomatis read-only
      // bila user adalah admin biasa.
      readOnlyProps: canMutate ? {} : { isReadOnly: true },
      // Untuk komponen yang tidak punya isReadOnly (Select, NumberInput,
      // react-select, dsb) — pakai isDisabled.
      disabledProps: canMutate ? {} : { isDisabled: true },
    };
  }, [level]);
};
